/**
 * Icon Picker Component for Puck Editor
 *
 * Allows users to select from a curated list of Lucide icons
 */

import React, { useEffect, useRef, useState } from "react";
import { FieldLabel } from "@puckeditor/core";
import { LUCIDE_ICONS } from "../../lib/lucide-icons";

interface IconPickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

// Curated list of commonly used service icons
const COMMON_ICONS = [
    { label: "Truck", name: "Truck" },
    { label: "Package Check", name: "PackageCheck" },
    { label: "Trash", name: "Trash2" },
    { label: "Wrench", name: "Wrench" },
    { label: "Lightning", name: "Zap" },
    { label: "Home", name: "Home" },
    { label: "Wind", name: "Wind" },
    { label: "Flame", name: "Flame" },
    { label: "Snowflake", name: "Snowflake" },
    { label: "Droplet", name: "Droplet" },
    { label: "Lightbulb", name: "Lightbulb" },
    { label: "Settings", name: "Settings" },
    { label: "Tool", name: "Tool" },
    { label: "Shield", name: "Shield" },
    { label: "Check Circle", name: "CheckCircle" },
    { label: "Star", name: "Star" },
    { label: "Phone", name: "Phone" },
    { label: "Mail", name: "Mail" },
    { label: "Map Pin", name: "MapPin" },
    { label: "Calendar", name: "Calendar" },
    { label: "Clock", name: "Clock" },
    { label: "Users", name: "Users" },
    { label: "Heart", name: "Heart" },
    { label: "Award", name: "Award" },
    { label: "Briefcase", name: "Briefcase" },
    { label: "Car", name: "Car" },
    { label: "Wifi", name: "Wifi" },
    { label: "Lock", name: "Lock" },
    { label: "Key", name: "Key" },
    { label: "Hammer", name: "Hammer" },
  ],

  IconPicker: React.FC<IconPickerProps> = ({ value = "", onChange, label }) => {
    const [isOpen, setIsOpen] = useState(false),
      [dropdownPos, setDropdownPos] = useState({ left: 0, top: 0, width: 0 }),
      buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPos({
          left: rect.left,
          top: rect.bottom + 4,
          width: rect.width,
        });
      }
    }, [isOpen]);

    const renderIcon = (iconName: string) => {
        const IconComponent = LUCIDE_ICONS[iconName];
        if (!IconComponent) {
          return null;
        }
        return <IconComponent size={20} strokeWidth={1.5} />;
      },

      selectedIcon = COMMON_ICONS.find((icon) => icon.name === value);

    return (
      <FieldLabel label={label ?? "Icon"} el="div">
        <div style={{ position: "relative" }}>
          {/* Selected Icon Display / Trigger Button */}
          <button
            ref={buttonRef}
            type="button"
            onClick={() => {
              setIsOpen(!isOpen);
            }}
            style={{
              alignItems: "center",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "6px",
              color: "#0D1015",
              cursor: "pointer",
              display: "flex",
              fontSize: "14px",
              gap: "8px",
              justifyContent: "space-between",
              lineHeight: "1.5",
              padding: "8px 12px",
              width: "100%",
            }}
          >
            <div style={{ alignItems: "center", display: "flex", gap: "8px" }}>
              {value && renderIcon(value)}
              <span>{selectedIcon?.label || "Select an icon"}</span>
            </div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              style={{
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            >
              <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Dropdown Panel */}
          {isOpen && (
            <>
              {/* Backdrop */}
              <div
                onClick={() => {
                  setIsOpen(false);
                }}
                style={{
                  bottom: 0,
                  left: 0,
                  position: "fixed",
                  right: 0,
                  top: 0,
                  zIndex: 999,
                }}
              />

              {/* Icon Grid */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  left: `${dropdownPos.left}px`,
                  maxHeight: "300px",
                  minWidth: "280px",
                  overflowY: "auto",
                  padding: "8px",
                  position: "fixed",
                  top: `${dropdownPos.top}px`,
                  width: `${dropdownPos.width}px`,
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gap: "4px",
                    gridTemplateColumns: "repeat(4, 1fr)",
                  }}
                >
                  {COMMON_ICONS.map((icon) => (
                    <button
                      key={icon.name}
                      type="button"
                      onClick={() => {
                        onChange(icon.name);
                        setIsOpen(false);
                      }}
                      title={icon.label}
                      style={{
                        alignItems: "center",
                        backgroundColor: value === icon.name ? "#EEF2FF" : "#FFFFFF",
                        border: "1px solid",
                        borderColor: value === icon.name ? "#6366F1" : "#E5E7EB",
                        borderRadius: "6px",
                        color: value === icon.name ? "#6366F1" : "#374151",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        padding: "12px",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (value !== icon.name) {
                          e.currentTarget.style.backgroundColor = "#F9FAFB";
                          e.currentTarget.style.borderColor = "#D1D5DB";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (value !== icon.name) {
                          e.currentTarget.style.backgroundColor = "#FFFFFF";
                          e.currentTarget.style.borderColor = "#E5E7EB";
                        }
                      }}
                    >
                      {renderIcon(icon.name)}
                    </button>
                  ))}
                </div>

                {/* Clear Selection Button */}
                {value && (
                  <button
                    type="button"
                    onClick={() => {
                      onChange("");
                      setIsOpen(false);
                    }}
                    style={{
                      backgroundColor: "#FEF2F2",
                      border: "1px solid #FEE2E2",
                      borderRadius: "4px",
                      color: "#DC2626",
                      cursor: "pointer",
                      fontSize: "13px",
                      marginTop: "8px",
                      padding: "8px",
                      width: "100%",
                    }}
                  >
                    Clear Selection
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </FieldLabel>
    );
  };

export { IconPicker };
